import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  TableColumn,
  StandardizedTable,
  formatNumber,
  formatCurrency,
  TrendIndicator,
  TableRow,
} from "@/components/ui/standardized-table";
import { getRegionsSalesApiV1RegionsSalesGet } from "@/backend-sdk/client/sdk.gen";
import type { RegionsSalesResponse } from "@/backend-sdk/client/types.gen";
import { useAccount } from "@/contexts/account.context";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import type { TableQueryDTO } from "@/types/table-query";
import { useDrilldownOptional } from "@/features/drilldown";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { heyApiClient } from "@/lib/hey-api-client";
import { ValorIndisponivel } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";

/** Sessões, receita e conversão por região saem do GA4. */
const FONTES_DA_TABELA: readonly RequisitoDeFonte[] = ["ga4"];

const VendasEstadoCard: React.FC = () => {
  const { selectedAccount } = useAccount();
  const { filter: globalFilter } = useGlobalFilter();
  const drilldown = useDrilldownOptional();
  // Começa carregando: o estado inicial é um rodapé zerado, que sem o véu parecia resposta.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const erro = error !== null;
  const [data, setData] = useState<RegionsSalesResponse>({
    items: [],
    pagination: { page: 1, page_size: 10, total: 0, total_pages: 0 },
    currency_symbol: "R$",
    percentage_symbol: "%",
    total_row: {
      conversion_rate: 0,
      conversion_rate_change_percentage: 0,
      conversion_rate_data_type: "PERCENT",
      conversion_rate_trend_indicator: "neutral",
      sessions: 0,
      sessions_change_percentage: 0,
      sessions_data_type: "NUMBER",
      sessions_trend_indicator: "neutral",
      total_revenue: 0,
      total_revenue_change_percentage: 0,
      total_revenue_data_type: "CURRENCY",
      total_revenue_trend_indicator: "neutral",
      transactions: 0,
      transactions_data_type: "NUMBER",
    },
  });
  const [page, setPage] = useState<number>(1);
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});
  const [dimension, setDimension] = useState<"region" | "city">("region");

  const canDrillUp = dimension === "city";
  const canDrillDown = dimension === "region";

  const handleDrillUp = useCallback(() => {
    setDimension("region");
  }, []);

  const handleDrillDown = useCallback(() => {
    setDimension("city");
  }, []);

  // Handler para clique na linha (cross-filtering com Ctrl+click multi-select)
  const handleRowClick = useCallback(
    (row: TableRow, index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;

      const rawValue = (
        dimension === "region" ? row.region : row.city
      ) as string;
      if (!rawValue || rawValue === "N/D") return;

      const isMultiSelect = !!(event?.ctrlKey || event?.metaKey);
      drilldown.selectRow(dimension, rawValue, rawValue, isMultiSelect);
    },
    [drilldown, dimension],
  );

  // Verifica se uma linha está selecionada
  const isRowSelected = useCallback(
    (row: TableRow, index: number): boolean => {
      if (!drilldown) return false;
      const rawValue = (
        dimension === "region" ? row.region : row.city
      ) as string;
      return drilldown.isSelected(dimension, rawValue);
    },
    [drilldown, dimension],
  );

  const fetchData = useCallback(
    async (pg: number = 1) => {
      if (!globalFilter) return;
      try {
        setLoading(true);
        setError(null);
        const startDate = formatDateToISO(globalFilter.dateRange.startDate);
        const endDate = formatDateToISO(globalFilter.dateRange.endDate);
        const comparison =
          globalFilter.comparison?.type === "previousYear"
            ? "previous_year"
            : "previous_period";

        const { data: resp } = await getRegionsSalesApiV1RegionsSalesGet({
          client: heyApiClient,
          throwOnError: true,
          query: {
            start_date: startDate,
            end_date: endDate,
            comparison,
            dimension,
            customer_uuid:
              selectedAccount?.customer_uuid ||
              selectedAccount?.id ||
              localStorage.getItem("active_customer_uuid") ||
              undefined,
            page: pg,
            page_size: 10,
            q: tableQuery?.q,
            sort_by: tableQuery?.sortBy,
            sort_dir: tableQuery?.sortDir,
            filters: tableQuery?.filters?.length
              ? JSON.stringify(tableQuery.filters)
              : undefined,
          },
        });
        setData(
          resp || {
            items: [],
            pagination: { page: 1, page_size: 10, total: 0, total_pages: 0 },
            total_row: {
              conversion_rate: 0,
              conversion_rate_change_percentage: 0,
              conversion_rate_trend_indicator: "neutral",
              sessions: 0,
              sessions_change_percentage: 0,
              sessions_trend_indicator: "neutral",
              total_revenue: 0,
              total_revenue_change_percentage: 0,
              total_revenue_trend_indicator: "neutral",
              transactions: 0,
            },
          },
        );
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Erro ao carregar");
      } finally {
        setLoading(false);
      }
    },
    [selectedAccount, globalFilter, tableQuery, dimension],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(1);
  }, [globalFilter, selectedAccount, drilldown?.apiFiltersKey, dimension]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchData(page);
  }, [fetchData, page]);

  // Linha de total (vinda do backend, calculada no conjunto completo filtrado)
  const totalRow = useMemo(() => {
    const backendTotal = data.total_row;
    if (!backendTotal || typeof backendTotal !== "object") return undefined;
    return {
      ...backendTotal,
      [dimension]: backendTotal?.[dimension] || "Total Geral",
      // O rodapé não desenha as três variações — só as linhas as têm.
      sessions_change_percentage: null,
      total_revenue_change_percentage: null,
      conversion_rate_change_percentage: null,
    };
  }, [data, dimension]);

  const renderDeltaCell = useCallback((value: unknown): React.ReactNode => {
    const change = typeof value === "number" ? value : null;
    if (change === null || Number.isNaN(change)) {
      return <span className="text-muted-foreground">—</span>;
    }
    return <TrendIndicator value={change} />;
  }, []);

  // Configuração da tabela padronizada
  const columns: TableColumn[] = useMemo(
    () => [
      {
        key: dimension,
        header: dimension === "region" ? "Região" : "Cidade",
        align: "left",
        render: (value, item, index, isTotal) =>
          isTotal ? value : `${(page - 1) * 10 + index + 1}. ${value}`,
      } as TableColumn,
      {
        key: "sessions",
        header: "Sessões",
        type: "number",
        visible: false,
        render: (value) => formatNumber(value),
      },
      {
        key: "sessions_change_percentage",
        header: "% Δ",
        align: "right" as const,
        visible: false,
        render: renderDeltaCell,
      },
      {
        key: "conversion_rate",
        header: "TX Conversão",
        align: "right",
        type: "percent",
        render: (value) => {
          const numeric =
            typeof value === "number" ? value : Number(value || 0);
          return !Number.isFinite(numeric) || numeric === 0 ? (
            <ValorIndisponivel fontes={FONTES_DA_TABELA} erro={erro} />
          ) : (
            `${numeric.toFixed(2)}%`
          );
        },
      },
      {
        key: "conversion_rate_change_percentage",
        header: "% Δ",
        align: "right" as const,
        visible: false,
        render: renderDeltaCell,
      },
      {
        key: "total_revenue",
        header: "Receita total",
        type: "number",
        render: (value) => formatCurrency(value),
      },
      {
        key: "total_revenue_change_percentage",
        header: "% Δ",
        align: "right" as const,
        visible: false,
        render: renderDeltaCell,
      },
    ],
    [dimension, page, renderDeltaCell, erro],
  );

  return (
    <StandardizedTable
      title={
        <div className="flex items-center justify-between gap-3 w-full">
          <div className="flex items-center gap-2">
            <span className="font-semibold">VENDAS POR ESTADO</span>
            <span className="text-xs text-muted-foreground">
              ({dimension === "region" ? "Região" : "Cidade"})
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDrillUp}
              disabled={!canDrillUp}
              className="h-7 px-2"
              title={canDrillUp ? "Drill up" : "Nível mais alto"}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDrillDown}
              disabled={!canDrillDown}
              className="h-7 px-2"
              title={canDrillDown ? "Drill down" : "Nível mais baixo"}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
        </div>
      }
      description={
        drilldown
          ? "Clique em uma linha para filtrar a página inteira"
          : undefined
      }
      columns={columns}
      // As colunas usam a chave que o servidor publica, sem renomear: em `queryMode="server"`
      // o `sort_by` que a tabela emite é a chave da coluna, e uma chave que só existe aqui
      // pediria ao servidor uma ordenação por um campo que a resposta dele não tem.
      data={data.items || []}
      loading={loading}
      total={totalRow}
      pagination={{
        current: data?.pagination?.page || 1,
        pageSize: data?.pagination?.page_size || 10,
        total: data?.pagination?.total || 0,
        onPageChange: (p) => setPage(p),
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setPage(1);
      }}
      onRowClick={drilldown ? handleRowClick : undefined}
      selectedRowIndex={drilldown ? isRowSelected : undefined}
      clickableRows={!!drilldown}
    />
  );
};

export default VendasEstadoCard;
